import { LAYOUTS } from '../../engine/layouts.js';
import { TARGET_ERROR_RATE_RANGE, TARGET_SPEED_RANGE } from '../../engine/grading.js';
import type { Settings } from '../../store/ProgressStore.js';
import { store, useStoreRevision } from '../store';

export function SettingsView() {
  useStoreRevision();
  const settings = store.getSettings();
  const update = (patch: Partial<Settings>) => store.updateSettings(patch);

  return (
    <>
      <div className="card">
        <h2>Nastavenia</h2>
        <p className="muted small" style={{ marginTop: 0 }}>
          Zmena nastavení sa uloží do tohto prehliadača a platí pre ďalšie cvičenia.
        </p>

        <h3>Rozloženie klávesnice</h3>
        <p className="muted small">
          Ak sú popisy kláves nepresné, appka si ich sama opraví podľa toho, čo naozaj píšeš —
          alebo ich nastav ručne v <a href="#/kalibracia">kalibrácii</a>.
        </p>
        <div className="row">
          {Object.values(LAYOUTS).map((layout) => (
            <label key={layout.id} className="row" style={{ gap: 6 }}>
              <input
                type="radio"
                name="layout"
                checked={settings.layoutId === layout.id}
                onChange={() => update({ layoutId: layout.id })}
              />
              {layout.name}
            </label>
          ))}
        </div>

        <h3>Režim chýb</h3>
        <div className="row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
          <label className="row" style={{ gap: 6 }}>
            <input
              type="radio"
              name="errorMode"
              checked={settings.errorMode === 'correct'}
              onChange={() => update({ errorMode: 'correct' })}
            />
            <span>
              <strong>Chybu treba opraviť</strong> — kurzor stojí, kým nenapíšeš správny znak.
              <span className="muted small"> Vhodné na učenie.</span>
            </span>
          </label>
          <label className="row" style={{ gap: 6 }}>
            <input
              type="radio"
              name="errorMode"
              checked={settings.errorMode === 'flow'}
              onChange={() => update({ errorMode: 'flow' })}
            />
            <span>
              <strong>Plynulé písanie</strong> — chyba sa počíta a ide sa ďalej.
              <span className="muted small"> Vhodné na testy a rýchlosť.</span>
            </span>
          </label>
        </div>

        <h3>Pomocníky</h3>
        <label className="row" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={settings.showKeyboard}
            onChange={(event) => update({ showKeyboard: event.target.checked })}
          />
          Zobrazovať vizuálnu klávesnicu
        </label>
        <label className="row" style={{ gap: 6, marginTop: 8 }}>
          <input
            type="checkbox"
            checked={settings.allowBackspace}
            onChange={(event) => update({ allowBackspace: event.target.checked })}
          />
          Povoliť Backspace (vypnuté = Backspace preskočí znak a počíta sa chyba)
        </label>
        <label className="row" style={{ gap: 6, marginTop: 8 }}>
          <input
            type="checkbox"
            checked={settings.tolerateDiacritics}
            onChange={(event) => update({ tolerateDiacritics: event.target.checked })}
          />
          Tolerovať diakritiku („s“ namiesto „š“ prejde, ale započíta sa ako mäkká chyba)
        </label>

        <h3>Metrika a známkovanie</h3>
        <p className="muted small">
          Podľa metodiky predlohy: úder nie je stlačenie klávesy. Číslice a veľké písmená sa
          počítajú za dva údery (v skupine číslic sa Shift počíta raz), <code>ď</code> za tri
          a veľké písmená s mäkčeňom za štyri. Známkuje sa zvlášť rýchlosť a chybovosť a platí
          <strong> horšia</strong> z nich.
        </p>
        <label className="row" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={settings.officialStrokes}
            onChange={(event) => update({ officialStrokes: event.target.checked })}
          />
          Počítať údery podľa oficiálnej metodiky (vypnuté = jedno stlačenie klávesy = jeden úder)
        </label>

        <label className="row" style={{ gap: 8, marginTop: 10 }}>
          Koeficient trestných bodov za chybu:
          <input
            type="number"
            min={0}
            max={50}
            step={1}
            value={settings.netPenalty}
            onChange={(event) => update({ netPenalty: Math.max(0, Number(event.target.value) || 0) })}
            style={{ width: 80 }}
          />
          <span className="muted small">
            čistá rýchlosť = hrubé údery − (chyby × koeficient). Metodika predlohy používa 10.
          </span>
        </label>

        <label className="row" style={{ gap: 8, marginTop: 10 }}>
          Cieľová rýchlosť:
          <input
            type="number"
            min={TARGET_SPEED_RANGE.min}
            max={TARGET_SPEED_RANGE.max}
            step={5}
            value={settings.targetSpeed}
            onChange={(event) =>
              update({
                targetSpeed: Math.min(
                  TARGET_SPEED_RANGE.max,
                  Math.max(TARGET_SPEED_RANGE.min, Number(event.target.value) || TARGET_SPEED_RANGE.min),
                ),
              })
            }
            style={{ width: 90 }}
          />
          <span className="muted small">
            čistých úderov/min ({TARGET_SPEED_RANGE.min}–{TARGET_SPEED_RANGE.max}). Predvolené v predlohe: 140.
          </span>
        </label>

        <label className="row" style={{ gap: 8, marginTop: 10 }}>
          Cieľová chybovosť:
          <input
            type="number"
            min={TARGET_ERROR_RATE_RANGE.min}
            max={TARGET_ERROR_RATE_RANGE.max}
            step={0.1}
            value={settings.targetErrorRate}
            onChange={(event) => {
              const value = Number(event.target.value) || TARGET_ERROR_RATE_RANGE.min;
              update({
                targetErrorRate: Math.min(
                  TARGET_ERROR_RATE_RANGE.max,
                  Math.max(TARGET_ERROR_RATE_RANGE.min, Math.round(value * 10) / 10),
                ),
              });
            }}
            style={{ width: 90 }}
          />
          <span className="muted small">
            % ({TARGET_ERROR_RATE_RANGE.min}–{TARGET_ERROR_RATE_RANGE.max}). Predvolené v predlohe: 0,3 %.
          </span>
        </label>

        <h3>Vzhľad</h3>
        <label className="row" style={{ gap: 8 }}>
          Veľkosť písma:
          <select
            value={settings.fontSize}
            onChange={(event) => update({ fontSize: event.target.value as Settings['fontSize'] })}
          >
            <option value="sm">menšia</option>
            <option value="md">štandardná</option>
            <option value="lg">väčšia (napr. dyslektické písmo)</option>
          </select>
        </label>
      </div>

      <div className="card">
        <h2>Prečo tu nie sú účty ani cookies</h2>
        <p className="muted small" style={{ marginBottom: 0 }}>
          Aplikácia úmyselne neposiela žiadne dáta na server: žiadne cookies, žiadna analytika,
          žiadne reklamy. Pokrok sa ukladá do prehliadača a dá sa preniesť súborom
          (<a href="#/pokrok">Pokrok → Záloha</a>). Ak by v budúcnosti pribudli účty a školský
          režim, rozhranie úložiska je na to pripravené a obrazovky sa nemenia.
        </p>
      </div>
    </>
  );
}
